// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useEffect, useRef } from 'react';

import { ArrowRight, StopFilledAlt } from '@carbon/icons-react';
import ContextChipsRow from './ContextChipsRow';
import type { AlarmContextSeed } from '../../types';

interface ChatInputProps {
  inputValue: string;
  setInputValue: (_value: string) => void;
  isThinking: boolean;
  /** Aborts the in-flight answer; shown in place of Send while thinking. */
  onStop: () => void;
  onSubmit: (_e: React.FormEvent | React.KeyboardEvent) => void;
  /** Overrides the prompt text. */
  placeholder?: string;
  /** The alarm this thread is scoped to, shown as a removable context chip. */
  threadSeed: AlarmContextSeed | null;
  /** Whether `threadSeed` has already gone out on a request. */
  contextSeedSent: boolean;
  /** The alarm on screen right now, if any — offered as an addable context chip. */
  pageAlarmContext: AlarmContextSeed | null;
  onAttachContext: (_seed: AlarmContextSeed) => void;
  onRemoveContext: () => void;
}

/** Stop takes Send's place, so a double-click or held Enter must not hit it at once. */
const STOP_ARM_DELAY_MS = 500;

const DEFAULT_PLACEHOLDER = 'Ask EnergyFluo anything...';

function ChatInput({
  inputValue,
  setInputValue,
  isThinking,
  onStop,
  onSubmit,
  placeholder = DEFAULT_PLACEHOLDER,
  threadSeed,
  contextSeedSent,
  pageAlarmContext,
  onAttachContext,
  onRemoveContext,
}: ChatInputProps) {
  const stopShownAt = useRef(0);
  useEffect(() => {
    if (isThinking) stopShownAt.current = Date.now();
  }, [isThinking]);

  return (
    <form className="akh-input-form" onSubmit={onSubmit}>
      <ContextChipsRow
        threadSeed={threadSeed}
        contextSeedSent={contextSeedSent}
        pageAlarmContext={pageAlarmContext}
        onAttach={onAttachContext}
        onRemove={onRemoveContext}
        disabled={isThinking}
      />
      <div className="akh-input-row">
        <textarea
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          placeholder={placeholder}
          className="akh-input"
          rows={2}
          aria-label="Type your message to the AI assistant"
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              onSubmit(e);
            }
          }}
        />
        {isThinking ? (
          <button
            key="stop"
            type="button"
            className="akh-send-btn"
            onClick={() => {
              if (Date.now() - stopShownAt.current >= STOP_ARM_DELAY_MS) onStop();
            }}
            aria-label="Stop"
          >
            <StopFilledAlt size={20} />
          </button>
        ) : (
          <button
            key="send"
            type="submit"
            className="akh-send-btn"
            disabled={!inputValue.trim()}
            aria-label="Send"
          >
            <ArrowRight size={20} />
          </button>
        )}
      </div>
    </form>
  );
}

export default ChatInput;
